// CSS Modules - component class name prefix: CountrySwitcher__

<template>
  <div
    v-show="!loading"
    :class="[{ CountrySwitcher__container__active: countrySwitcherActive }, $style.container]"
  >
    <tippy
      ref="tippyRef"
      :arrow="true"
      :content-class="`${$style.layer} countrySwitcherPopper`"
      content-tag="div"
      :interactive="true"
      max-width="none"
      :on-hide="
        () => {
          countrySwitcherActive = false;
        }
      "
      :on-show="
        () => {
          countrySwitcherActive = true;
        }
      "
      placement="bottom-start"
      theme="wika light tooltip"
      :to="'#countrySwitcherTrigger'"
      trigger="click"
      :z-index="10000"
    >
      <template #content>
        <Button :class="$style.close" colour="white" is-close @close="closeCountrySwitcher" />
        <div :class="$style.content">
          <Tabs :tab-count="2">
            <template #1-title>
              {{ config.websitesTitle }}
            </template>
            <template #1-content>
              <div :class="$style.websites">
                <div :class="[$style.websitesControls, 'columns']">
                  <div class="column">
                    <Select
                      v-if="regionSelectValue"
                      :key="`sitesKey-${componentKey}`"
                      :class="$style.regionSelect"
                      colour="blue"
                      :select-options="siteSelectOptions"
                      :select-value="regionSelectValue || siteSelectValue"
                      @on-change="updateCountryButtons"
                    />
                  </div>
                  <div
                    class="column is-offset-one-third-tablet is-offset-half-desktop is-offset-three-fifths-widescreen"
                  >
                    <Button
                      :class="$style.corporate"
                      colour="orange"
                      :href="config.corporateUrl"
                      is-link
                      :text="config.corporateTitle"
                    />
                  </div>
                </div>
                <div :class="$style.countries">
                  <div
                    v-for="(country, index) in countryButtons"
                    :key="`CountryButton${country.name}${index}`"
                  >
                    <CountryButton
                      :country="country.name"
                      :flag="country.flag"
                      :label="countryButtonLabel"
                      :languages="country.languages"
                    />
                  </div>
                </div>
              </div>
            </template>
            <template #2-title>
              {{ config.locationsTitle }}
            </template>
            <template #2-content>
              <div :class="$style.locations">
                <div :class="[$style.locationsControls, 'columns is-multiline']">
                  <div class="column is-one-third-tablet is-one-quarter-desktop">
                    <Select
                      v-if="regionSelectValue"
                      :class="$style.regionSelect"
                      colour="blue"
                      :select-options="regionSelectOptions"
                      :select-value="regionSelectValue"
                      @on-change="updateCountries"
                    />
                  </div>
                  <div class="column is-one-third-tablet is-one-quarter-desktop">
                    <Select
                      v-if="countrySelectValue"
                      :key="`countryKey-${componentKey}`"
                      :class="$style.countrySelect"
                      colour="white"
                      :placeholder="countrySelectPlaceholderRef"
                      :select-options="countrySelectOptions"
                      :select-value="
                        [...countrySelectOptions].includes(countrySelectValue)
                          ? countrySelectValue
                          : countrySelectPlaceholderRef
                      "
                      @on-change="updateContacts"
                    />
                  </div>
                  <div
                    class="column is-one-third-tablet is-one-quarter-desktop is-offset-one-quarter-desktop"
                  >
                    <Button
                      :class="$style.headQuarters"
                      colour="orange"
                      :href="config.corporateUrl"
                      is-link
                      :text="config.headquarterTitle"
                    />
                  </div>
                </div>
                <div
                  :class="[
                    $style.contacts,
                    {
                      [$style.contactsWithSpace]: contacts && contacts.length > contactsToShow,
                    },
                  ]"
                >
                  <div v-if="contacts">
                    <VueAgile
                      ref="slider"
                      :dots="false"
                      :infinite="false"
                      :nav-buttons="contacts && contacts.length > 1"
                      :responsive="[
                        {
                          breakpoint: 480,
                          settings: {
                            slidesToShow: 2,
                            navButtons: contacts && contacts.length > 2,
                          },
                        },
                        {
                          breakpoint: 768,
                          settings: {
                            slidesToShow: 3,
                            navButtons: contacts && contacts.length > 3,
                          },
                        },
                        {
                          breakpoint: 1024,
                          settings: {
                            slidesToShow: 4,
                            navButtons: contacts && contacts.length > 4,
                          },
                        },
                      ]"
                      :slides-to-show="1"
                      @breakpoint="resetSlider"
                    >
                      <div v-for="(contact, index) in contacts" :key="index">
                        <h6 v-if="contact.company">
                          {{ contact.company }}
                        </h6>
                        <address
                          v-if="contact.address"
                          v-html="
                            `${contact.address.replace(/(?:\r\n|\r|\n)/g, '<br>')}<br>${
                              contact.country
                            }`
                          "
                        />
                        <dl v-if="contact.phone || contact.fax">
                          <dt v-if="contact.phone">Tel.:</dt>
                          <dd v-if="contact.phone">
                            {{ contact.phone }}
                          </dd>
                          <br />
                          <dt v-if="contact.fax">Fax:</dt>
                          <dd v-if="contact.fax">
                            {{ contact.fax }}
                          </dd>
                        </dl>
                        <a v-if="contact.email" :href="`mailto:${contact.email}`">{{
                          contact.email
                        }}</a>
                        <br />
                        <a v-if="contact.website" :href="contact.website" target="_blank">{{
                          contact.website_text !== '' ? contact.website_text : contact.website
                        }}</a>
                      </div>
                    </VueAgile>
                  </div>
                </div>
              </div>
            </template>
          </Tabs>
        </div>
      </template>
    </tippy>
    <button :id="'countrySwitcherTrigger'" aria-expanded="false" :class="$style.toggle">
      <i class="fas fa-globe-americas" />
      <span>{{ config.currentCountry }}</span>
    </button>
    <span :class="$style.caret" />
  </div>
</template>

<script setup>
import { VueAgile } from 'vue-agile';
import Button from '../Button/Button.vue';
import CountryButton from '../CountryButton/CountryButton.vue';
import Select from '../Select/Select.vue';
import Tabs from '../Tabs/Tabs.vue';
import { regionDataUrl } from '../../shared/globals';
import * as Storage from '../../shared/Storage';
import { onMounted, onUnmounted, ref, computed } from 'vue';
import { useStoreTranslations } from '../../store/translations/translations';
import safeFetch from '../../utils/safeFetch';

const props = defineProps({
  config: {
    type: Object,
    default: () => {},
  },
});

const loading = ref(true);
const componentKey = ref(0);
const apiData = ref(null);
const contacts = ref([]);
const siteSelectOptions = ref([]);
const siteSelectValue = ref('');
const countryButtons = ref([]);
const regionSelectOptions = ref([]);
const regionSelectValue = ref(props.config.currentRegion);
const countrySelectOptions = ref([]);
const countrySelectValue = ref(props.config.currentCountry);
const countrySelectPlaceholderRef = ref('');
const countrySwitcherActive = ref(false);
const contactsToShow = ref(1);
const cookieName = ref('cookieCountry');
const slider = ref(null);
const tippyRef = ref(null);

const storeTranslations = useStoreTranslations();

const countryButtonLabel = computed(() => {
  return (
    props.config.languageTitle ||
    storeTranslations.translations?.['countryLanguageSwitcher.language']
  );
});

const calcContactsToShow = () => {
  contactsToShow.value =
    window.innerWidth >= 1024 ? 4 : window.innerWidth >= 768 ? 3 : window.innerWidth >= 480 ? 2 : 1;
};

const resetSlider = () => {
  slider.value.goTo(0);
};

const closeCountrySwitcher = () => {
  tippyRef.value.hide();
  countrySwitcherActive.value = false;
};

const getContacts = (url) => {
  contacts.value = null;
  safeFetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then((data) => {
      contacts.value = data.contacts;
    })
    .catch((error) => {
      console.error(error);
    });
};

const updateContacts = (value) => {
  countrySelectValue.value = value;

  const region = apiData.value.regions.find((region) => region.name === regionSelectValue.value);

  if (region) {
    const country = region.countries.find((country) => country.name === value);

    if (country) {
      getContacts(country.contacts);
    }
  }
};

const initApiData = (data) => {
  apiData.value = data;
  countrySelectPlaceholderRef.value = props.config.countrySelectPlaceholder;
  let countrySelectOptionsRaw = [];

  for (let region of apiData.value.regions) {
    siteSelectOptions.value.push(region.name);
    regionSelectOptions.value.push(region.name);

    if (region.name === props.config.currentRegion) {
      for (let country of region.countries) {
        countrySelectOptionsRaw.push(country.name);

        if (country.flag && country.languages && country.languages.length !== 0) {
          countryButtons.value.push(country);
        }

        if (country.name === props.config.currentCountry) {
          getContacts(country.contacts);
        }
      }
    }
  }
  countrySelectOptions.value = countrySelectOptionsRaw;
  loading.value = false;
};

const updateCountryButtons = (value) => {
  siteSelectValue.value = value;
  const region = apiData.value?.regions.find((region) => region.name === value);

  if (region) {
    countryButtons.value.length = 0;

    for (let country of region.countries) {
      if (country.flag && country.languages && country.languages.length !== 0) {
        countryButtons.value.push(country);
      }
    }
  }
};

const updateCountries = (value) => {
  regionSelectValue.value = value;
  countrySelectOptions.value.length = 0;
  contacts.value = [];

  const region = apiData.value.regions.find((region) => region.name === value);

  if (region) {
    for (let country of region.countries) {
      countrySelectOptions.value.push(country.name);
    }
  }
};

onMounted(() => {
  window.addEventListener('resize', calcContactsToShow.value);
  if (window.currentCountry) {
    Storage.setSessionCookie(cookieName.value, window.currentCountry);
  }
  calcContactsToShow();
  let dataUrl = '';
  window.regionDataUrl ? (dataUrl = window.regionDataUrl) : (dataUrl = regionDataUrl);
  safeFetch(dataUrl)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then((data) => initApiData(data))
    .catch((error) => {
      loading.value = false;
      console.error(error);
    });
});

onUnmounted(() => {
  window.removeEventListener('resize', calcContactsToShow.value);
});
</script>

<style module lang="scss">
@forward './CountrySwitcher.scss';
</style>
